import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, MapPin, Wrench, Package, Building2, Layers, Zap, Home, Droplets } from "lucide-react";
import { locations, services, materials, seoStats } from "@/data/seoData";

// SEO Mega Footer Component - Extended Version
const SEOFooter = () => {
  const [activeTab, setActiveTab] = useState("locations");
  const [expandedRegion, setExpandedRegion] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);

  const tabs = [
    { id: "locations", label: "Mestá a obce", icon: MapPin, count: `${seoStats.totalLocations}+` },
    { id: "services", label: "Služby", icon: Wrench, count: `${seoStats.totalServices}` },
    { id: "materials", label: "Materiály", icon: Package, count: `${seoStats.totalMaterials}+` },
    { id: "combinations", label: "Kombinácie", icon: Building2, count: `${seoStats.totalCombinations}+` }
  ];

  const serviceCategories = [
    { key: "hlavne", name: "Hlavné služby", icon: Home, color: "cyan" },
    { key: "stavebne", name: "Stavebné práce", icon: Building2, color: "orange" },
    { key: "strechy", name: "Strešné práce", icon: Layers, color: "cyan" },
    { key: "interier", name: "Interiérové práce", icon: Home, color: "orange" },
    { key: "instalcie", name: "Inštalácie", icon: Zap, color: "cyan" },
    { key: "exterier", name: "Exteriérové práce", icon: Layers, color: "orange" },
    { key: "specialne", name: "Špeciálne služby", icon: Droplets, color: "cyan" }
  ];

  // Generate top combinations
  const generateTopCombinations = () => {
    const combos = [];
    const topServices = ["Stavby na kľúč", "Holodomy", "Rekonštrukcie", "Zateplenie", "Zámková dlažba", "Bazény", "Strechy", "Sadrokartón"];
    const topLocations = [
      "Bratislava", "Petržalka", "Ružinov", "Nové Mesto", "Senec", 
      "Pezinok", "Modra", "Trnava", "Dunajská Streda", "Šamorín",
      "Malacky", "Stupava", "Galanta", "Nové Zámky", "Nitra"
    ];
    
    topServices.forEach(svc => {
      topLocations.forEach(loc => {
        combos.push(`${svc} ${loc}`);
      });
    });
    return combos;
  };

  return (
    <section className="py-16 bg-[#030303] border-t border-white/5" data-testid="seo-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="mono text-xs text-[#00E5FF] uppercase tracking-[0.2em]">
            Kompletné pokrytie regiónu
          </span>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mt-4">
            <span className="text-[#00E5FF]">{seoStats.totalCombinations}+</span> stavebných služieb vo vašom <span className="text-[#FF5E00]">meste</span>
          </h2>
          <p className="text-zinc-500 mt-4 max-w-2xl mx-auto">
            Poskytujeme {seoStats.totalServices} rôznych stavebných služieb v {seoStats.totalLocations}+ mestách a obciach západného Slovenska.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/[0.02] rounded-xl p-4 text-center border border-white/5">
            <span className="text-2xl font-black text-[#00E5FF]">{seoStats.totalLocations}+</span>
            <p className="text-zinc-500 text-sm">Miest a obcí</p>
          </div>
          <div className="bg-white/[0.02] rounded-xl p-4 text-center border border-white/5">
            <span className="text-2xl font-black text-[#FF5E00]">{seoStats.totalServices}</span>
            <p className="text-zinc-500 text-sm">Typov služieb</p>
          </div>
          <div className="bg-white/[0.02] rounded-xl p-4 text-center border border-white/5">
            <span className="text-2xl font-black text-[#00E5FF]">{seoStats.totalMaterials}+</span>
            <p className="text-zinc-500 text-sm">Materiálov</p>
          </div>
          <div className="bg-white/[0.02] rounded-xl p-4 text-center border border-white/5">
            <span className="text-2xl font-black text-[#FF5E00]">{seoStats.totalCombinations}+</span>
            <p className="text-zinc-500 text-sm">Kombinácií</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? "bg-[#00E5FF] text-black"
                  : "bg-white/5 text-zinc-400 hover:bg-white/10"
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                activeTab === tab.id ? "bg-black/20" : "bg-white/10"
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white/[0.02] rounded-2xl border border-white/5 p-6 lg:p-8 max-h-[600px] overflow-y-auto">
          <AnimatePresence mode="wait">
            {/* Locations Tab */}
            {activeTab === "locations" && (
              <motion.div
                key="locations"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                {Object.entries(locations).map(([key, region]) => (
                  <div key={key} className="border-b border-white/5 pb-4 last:border-0">
                    <button
                      onClick={() => setExpandedRegion(expandedRegion === key ? null : key)}
                      className="w-full flex items-center justify-between py-2 text-left group"
                    >
                      <span className="font-bold text-lg group-hover:text-[#00E5FF] transition-colors">
                        {region.name}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-500 text-sm">
                          {(region.districts?.length || 0) + (region.cities?.length || 0)} lokalít
                        </span>
                        <ChevronDown 
                          size={18} 
                          className={`text-zinc-500 transition-transform ${expandedRegion === key ? "rotate-180" : ""}`}
                        />
                      </div>
                    </button>
                    
                    <AnimatePresence>
                      {expandedRegion === key && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="flex flex-wrap gap-2 pt-4">
                            {(region.districts || region.cities || []).map((loc, i) => (
                              <a
                                key={i}
                                href={`#${loc.toLowerCase().replace(/\s+/g, '-')}`}
                                className="px-3 py-1.5 bg-white/5 hover:bg-[#00E5FF]/20 hover:text-[#00E5FF] rounded-lg text-sm text-zinc-400 transition-all"
                              >
                                {loc}
                              </a>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </motion.div>
            )}

            {/* Services Tab - Extended */}
            {activeTab === "services" && (
              <motion.div
                key="services"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6"
              >
                {serviceCategories.map((cat) => (
                  <div key={cat.key}>
                    <button
                      onClick={() => setExpandedCategory(expandedCategory === cat.key ? null : cat.key)}
                      className="w-full flex items-center justify-between mb-3 group"
                    >
                      <div className="flex items-center gap-2">
                        <cat.icon size={18} className={cat.color === "cyan" ? "text-[#00E5FF]" : "text-[#FF5E00]"} />
                        <h3 className={`font-bold text-lg ${cat.color === "cyan" ? "text-[#00E5FF]" : "text-[#FF5E00]"}`}>
                          {cat.name}
                        </h3>
                        <span className="text-zinc-500 text-sm">({services[cat.key]?.length || 0})</span>
                      </div>
                      <ChevronDown 
                        size={18} 
                        className={`text-zinc-500 transition-transform ${expandedCategory === cat.key ? "rotate-180" : ""}`}
                      />
                    </button>
                    
                    <AnimatePresence>
                      {(expandedCategory === cat.key || cat.key === "hlavne") && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="flex flex-wrap gap-2 pb-4">
                            {services[cat.key]?.map((svc, i) => (
                              <a
                                key={i}
                                href={`#${svc.slug}`}
                                className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                                  cat.color === "cyan"
                                    ? "bg-[#00E5FF]/10 hover:bg-[#00E5FF]/30 text-[#00E5FF]/80 hover:text-[#00E5FF]"
                                    : "bg-[#FF5E00]/10 hover:bg-[#FF5E00]/30 text-[#FF5E00]/80 hover:text-[#FF5E00]"
                                }`}
                              >
                                {svc.name}
                              </a>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </motion.div>
            )}

            {/* Materials Tab - Extended */}
            {activeTab === "materials" && (
              <motion.div
                key="materials"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                {Object.entries(materials).map(([category, items]) => (
                  <div key={category}>
                    <h3 className="font-bold text-sm uppercase tracking-wider text-[#00E5FF] mb-3">
                      {category === "muriva" && "Murivá a tvárnice"}
                      {category === "izolacie" && "Tepelné izolácie"}
                      {category === "strecha" && "Strešné krytiny"}
                      {category === "fasada" && "Fasádne materiály"}
                      {category === "podlahy" && "Podlahové systémy"}
                      {category === "dlazba" && "Dlažby a dláždenie"}
                      {category === "sadrokarton" && "Sadrokartón"}
                      {category === "oknaAdvere" && "Okná a dvere"}
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {items.map((item, i) => (
                        <a
                          key={i}
                          href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
                          className="px-2 py-1 bg-white/5 hover:bg-white/10 rounded text-xs text-zinc-400 hover:text-white transition-all"
                        >
                          {item}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </motion.div>
            )}

            {/* Combinations Tab - Extended */}
            {activeTab === "combinations" && (
              <motion.div
                key="combinations"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <p className="text-zinc-500 text-sm mb-6">
                  Vyberte si z <span className="text-[#00E5FF] font-bold">{seoStats.totalCombinations}+</span> kombinácií služieb a lokalít. 
                  Tu sú najčastejšie vyhľadávané:
                </p>
                <div className="flex flex-wrap gap-2 max-h-[400px] overflow-y-auto">
                  {generateTopCombinations().map((combo, i) => (
                    <a
                      key={i}
                      href={`#${combo.toLowerCase().replace(/\s+/g, '-')}`}
                      className="px-3 py-1.5 bg-white/5 hover:bg-[#00E5FF]/20 hover:text-[#00E5FF] rounded-lg text-sm text-zinc-400 transition-all"
                    >
                      {combo}
                    </a>
                  ))}
                </div>
                <p className="text-zinc-600 text-xs mt-6 text-center">
                  ... a ďalších {seoStats.totalCombinations - 120}+ kombinácií. Kontaktujte nás pre vašu lokalitu.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Quick Links Grid - Extended */}
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
          <QuickLinkCard title="Stavby na kľúč" links={["Bratislava", "Senec", "Pezinok", "Trnava"]} color="cyan" />
          <QuickLinkCard title="Holodomy" links={["Bratislava", "Senec", "Modra", "Galanta"]} color="orange" />
          <QuickLinkCard title="Rekonštrukcie" links={["Petržalka", "Ružinov", "Staré Mesto"]} color="cyan" />
          <QuickLinkCard title="Zateplenie" links={["Bratislava", "Senec", "Malacky"]} color="orange" />
          <QuickLinkCard title="Strechy" links={["Bratislava", "Pezinok", "Trnava"]} color="cyan" />
          <QuickLinkCard title="Sadrokartón" links={["Bratislava", "Senec", "Nitra"]} color="orange" />
          <QuickLinkCard title="Bazény" links={["Senec", "Modra", "Šamorín"]} color="cyan" />
          <QuickLinkCard title="Ploty" links={["Bratislava", "Senec", "Stupava"]} color="orange" />
        </div>

        {/* Bottom SEO Text */}
        <div className="mt-12 text-center">
          <p className="text-zinc-600 text-xs max-w-4xl mx-auto leading-relaxed">
            SuperMajstri poskytuje komplexné stavebné služby v Bratislavskom, Trnavskom a Nitrianskom kraji. 
            Špecializujeme sa na stavby rodinných domov na kľúč, holodomy, rekonštrukcie bytov a domov, 
            zatepľovanie fasád, pokládku zámkovej dlažby, výstavbu bazénov, strešné práce, sadrokartónové práce, 
            murárske práce, elektroinštalácie, vodoinštalácie a mnoho ďalších stavebných činností. 
            Pôsobíme v mestách Bratislava, Senec, Pezinok, Modra, Trnava, Dunajská Streda, Šamorín, Galanta, 
            Malacky, Stupava, Nové Zámky, Komárno, Nitra a v stovkách ďalších obcí západného Slovenska.
          </p>
        </div>
      </div>
    </section>
  );
};

// Quick Link Card Component
const QuickLinkCard = ({ title, links, color }) => {
  return (
    <div className="bg-white/[0.02] rounded-xl p-3 border border-white/5 hover:border-white/10 transition-colors">
      <h4 className={`font-bold text-xs mb-2 ${color === "cyan" ? "text-[#00E5FF]" : "text-[#FF5E00]"}`}>
        {title}
      </h4>
      <ul className="space-y-0.5">
        {links.map((link, i) => (
          <li key={i}>
            <a 
              href={`#${title.toLowerCase().replace(/\s+/g, '-')}-${link.toLowerCase()}`}
              className="text-[10px] text-zinc-500 hover:text-white transition-colors"
            >
              {link}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SEOFooter;
